import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    effect,
    ViewChild,
    ViewEncapsulation,
    inject,
    output,
    model,
    signal,
    computed,
    input,
} from '@angular/core';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    IAccessRightUserFilter,
    SelectionFilterModel,
} from '@fi-shared/models';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import {
    FiAccessRightHelperService,
    IAccessRightsCoreFiltersErrors,
} from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { FiManualSelectionSectionComponent } from '@fi-shared/access-right-filters/fi-manual-selection-section/fi-manual-selection-section.component';
import { ModuleType } from '@fi-shared/types';
import { FiComponentsContainer } from '@fi-app/shared/fi-components-container';
import { SharingSettingsHelperService } from './sharing-settings-helper.service';
import { Observable } from 'rxjs';

export interface ISharingSettingsContentConfig {
    availableAccessRightFilterCollection: IAccessRightFilterCollection;
    accessRightUserFilter?: IAccessRightUserFilter;
    moduleType: ModuleType;
    businessUnitIdsOutOfScope?: number[];
    isItemCreator?: boolean;
    isRolesRequired?: boolean;
    showRolesSelect?: boolean;
    areasSectionHeaderText?: string;
    areasSectionDescriptionText?: string;
    rolesSectionHeaderText?: string;
    rolesSectionDescriptionText?: string;
    individualUnitSectionHeaderText?: string;
    individualUnitSectionDescriptionText?: string;
    showErrorTooltip$?: Observable<boolean>;
    scrollableElementRefs?: ElementRef<HTMLElement>[];
    showRolesCheckboxes?: boolean;
    quickSelectLabelId?: string;
}

@Component({
    selector: 'fi-sharing-settings-content',
    templateUrl: './fi-sharing-settings-content.component.html',
    styleUrls: ['./fi-sharing-settings-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-sharing-settings-content' },
    standalone: false,
    providers: [{ provide: FiComponentsContainer, useExisting: FiSharingSettingsContentComponent }],
})
export class FiSharingSettingsContentComponent extends FiComponentsContainer {
    public isShared = input.required<boolean>();
    private _isSharedEffect = effect(() => {
        const isShared = this.isShared();
        setTimeout(() => {
            this.fiManualSelectionSectionComponent?.updateTooltipVisibility();
        });
    });
    public config = input.required<ISharingSettingsContentConfig>();

    public accessRightCoreFilter = model.required<IAccessRightCoreFilter>();
    public savedQuickSelectionFilters = model<IAccessRightSelectionFiltersCollection[]>([]);
    public selectedFiltersCollection = model<IAccessRightSelectionFiltersCollection | undefined>(undefined);

    public validationErrorsChange = output<IAccessRightsCoreFiltersErrors>();

    @ViewChild('fiManualSelectionSectionComponent')
    protected fiManualSelectionSectionComponent: FiManualSelectionSectionComponent;

    private readonly accessRightService = inject(AccessRightService);
    private readonly fiAccessRightHelperService = inject(FiAccessRightHelperService);
    private readonly sharingSettingsHelper = inject(SharingSettingsHelperService);

    protected newSelectionFilterModel: SelectionFilterModel = new SelectionFilterModel();
    public isSaveAsNewQuickSelection = signal<boolean>(false);
    private _manualSelectionValidationErrors = signal<IAccessRightsCoreFiltersErrors>({});

    protected availableAccessRightCoreFilter = computed<IAccessRightCoreFilter>(() => {
        return this.config().availableAccessRightFilterCollection.AccessRightCoreFilter;
    });

    private doRoleIdsCheckForSaveFilter = computed<boolean>(() => {
        return this.config().showRolesSelect !== false || this.config().showRolesCheckboxes;
    });

    protected isUpdateQuickSelectionCollectionButtonVisible = computed<boolean>(() => {
        return this.sharingSettingsHelper.isUpdateQuickSelectionCollectionButtonVisible(
            this.isSaveAsNewQuickSelection(),
            this.selectedFiltersCollection(),
            this.accessRightCoreFilter(),
            this.doRoleIdsCheckForSaveFilter(),
        );
    });

    protected isNewQuickSelectionDisabled = computed<boolean>(() => {
        return this.sharingSettingsHelper.isNewQuickSelectionDisabled(
            this._manualSelectionValidationErrors(),
            this.accessRightCoreFilter(),
            this.selectedFiltersCollection(),
            this.doRoleIdsCheckForSaveFilter(),
        );
    });

    protected hasAreaRights = computed<boolean>(() => {
        return !!this.availableAccessRightCoreFilter().AreaIds.length || !!this.accessRightCoreFilter().AreaIds.length;
    });

    protected isBusinessAreasSelectionVisible = computed<boolean>(() => {
        return this.fiAccessRightHelperService.isBusinessAreasSelectionAvailable(
            this.availableAccessRightCoreFilter(),
            this.accessRightCoreFilter(),
            { ignoreClusters: true },
        );
    });

    protected isUnitsSectionHeaderVisible = computed<boolean>(() => {
        return this.isBusinessAreasSelectionVisible() || this.hasAreaRights();
    });

    // is used outside of the component
    public get newSelectionFilterModelName(): string {
        return this.newSelectionFilterModel?.Name ?? '';
    }
    public saveNewQuickSelection(): Promise<void> {
        const savePromise = this.isSaveAsNewQuickSelection()
            ? this.accessRightService.SaveSelectionFilter(this.newSelectionFilterModel, this.config().moduleType)
            : Promise.resolve();

        return savePromise
            .then(() => {
                return this.accessRightService.GetSelectionFiltersForPublication(this.config().moduleType, true);
            })
            .then((updatedFilters) => {
                this.savedQuickSelectionFilters.set(updatedFilters);
            });
    }
    //

    protected updateQuickSelection(): void {
        if (!this.selectedFiltersCollection) {
            return;
        }

        this.sharingSettingsHelper
            .updateQuickSelection(
                this.selectedFiltersCollection(),
                this.accessRightCoreFilter(),
                this.config().moduleType,
            )
            .then(() => {
                this.updateSavedQuickSelectionFilter();
            });
    }

    protected deleteQuickSelection(filterCollection: IAccessRightSelectionFiltersCollection): void {
        this.sharingSettingsHelper
            .deleteQuickSelection(filterCollection, this.savedQuickSelectionFilters(), this.config().moduleType)
            .then((updatedFilters) => {
                this.savedQuickSelectionFilters.set(updatedFilters);
            });
    }

    protected setManualSelectionValidationErrors(validationErrors: IAccessRightsCoreFiltersErrors): void {
        this._manualSelectionValidationErrors.set(validationErrors);
        this.validationErrorsChange.emit(this._manualSelectionValidationErrors());
    }

    protected onSaveAsNewQuickSelectionChange(isSaveAsNewQuickSelection: boolean): void {
        this.isSaveAsNewQuickSelection.set(isSaveAsNewQuickSelection);
        if (!isSaveAsNewQuickSelection) {
            this.newSelectionFilterModel.Name = '';
            this.newSelectionFilterModel.IsDefault = false;
        } else {
            this.newSelectionFilterModel = this.sharingSettingsHelper.initNewSelectionFilterModel(
                this.accessRightCoreFilter(),
                this.config().accessRightUserFilter,
            );
        }
    }

    protected onSelectedFilterChange(selectedFiltersCollection: IAccessRightSelectionFiltersCollection): void {
        this.selectedFiltersCollection.set(selectedFiltersCollection);

        if (!isDefined(selectedFiltersCollection)) {
            return;
        }

        this.accessRightService.removeNonExistingSelectionFilter(
            selectedFiltersCollection.SelectionFilterSettingCollection,
            this.config().availableAccessRightFilterCollection,
        );

        this.accessRightCoreFilter.set(
            cloneDeep(selectedFiltersCollection.SelectionFilterSettingCollection.AccessRightCoreFilter),
        );

        setTimeout(() => {
            this.fiManualSelectionSectionComponent?.updateTooltipVisibility();
        });
    }

    protected onRoleIdsChange(roleIds: number[]): void {
        this.accessRightCoreFilter.update((arcf) => ({ ...arcf, RoleIds: roleIds }));
        setTimeout(() => {
            this.fiManualSelectionSectionComponent?.updateTooltipVisibility();
        });
    }

    protected onAccessRightCoreFilterChange(filter: IAccessRightCoreFilter): void {
        this.accessRightCoreFilter.update((arcf) => ({ ...arcf, ...filter }));
    }

    protected onBusinessUnitIdsChange(businessUnitIds: number[]): void {
        if (businessUnitIds) {
            this.accessRightCoreFilter.update((arcf) => ({ ...arcf, BusinessUnitIds: cloneDeep(businessUnitIds) }));
        }
        setTimeout(() => {
            this.fiManualSelectionSectionComponent?.updateTooltipVisibility();
        });
    }

    private updateSavedQuickSelectionFilter(): void {
        const selectedFiltersCollection = this.selectedFiltersCollection();
        if (!selectedFiltersCollection) {
            return;
        }

        this.savedQuickSelectionFilters.update((filters) => {
            return filters.map((f) => (f.Id === selectedFiltersCollection.Id ? selectedFiltersCollection : f));
        });
    }
}
